'use client'
import { NavRightArrow } from '@/svgs'
import DragDropIcon from '@/svgs/DragDropIcon'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toastError, toastSuccess } from '@/utils/toaster'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'

const allowedFileTypes = ['.jpg', '.png', '.pdf', '.csv', '.xls', '.docx']
const maxFileSize = 5 * 1024 * 1024

function ContactUsForm({ isContactUSPage = false }) {
   const fileInputRef = useRef(null)
   const [phoneNumber, setPhoneNumber] = useState()

   const [isLoading, setIsLoading] = useState(false)

   const [isDrag, setIsDrag] = useState(false)

   const {
      register,
      handleSubmit,
      setValue,
      getValues,
      watch,
      formState: { errors },
      reset,
      setError,
      clearErrors,
   } = useForm()
   const file = watch('file')
   register('mobile_number', { required: 'Phone number is required' })

   const onSubmit = async (data) => {
      if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
         return
      }
      setIsLoading(true)
      const newData = { ...data, mobile_number: phoneNumber }

      if (!window.grecaptcha) return

      await window.grecaptcha.ready(() => {
         window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
               action: 'submit',
            })
            .then(async (token) => {
               const formData = new FormData()

               Object.entries(newData).forEach(([key, value]) => {
                  formData.append(key, value)
               })
               formData.append('token', token)
               formData.append(
                  'subject',
                  'New contact enquiry received on benzatine'
               )

               try {
                  const res = await fetch(
                     `${process.env.NEXT_PUBLIC_BACKEND_URL}/contact_us/add`,
                     {
                        method: 'POST',
                        body: formData,
                     }
                  )

                  if (!res.ok) throw new Error('Server error')
                  const result = await res.json()
                  toastSuccess(result.msg)
                  reset()
                  setIsLoading(false)
               } catch (err) {
                  toastError(
                     'There was an error submitting the form. Please try again.'
                  )
                  setIsLoading(false)
               } finally {
                  setIsLoading(false)
               }
            })
      })
   }

   const validateAndSetFile = (file) => {
      if (!file) return

      const ext = '.' + file.name.split('.').pop().toLowerCase()
      if (!allowedFileTypes.includes(ext)) {
         setError('file', {
            type: 'manual',
            message: `Invalid file type. Allowed: ${allowedFileTypes.join(
               ', '
            )}`,
         })
         setValue('file', null)
         return
      }

      if (file.size > maxFileSize) {
         setError('file', {
            type: 'manual',
            message: 'File is too large. Maximum size is 5MB',
         })
         setValue('file', null)
         return
      }

      clearErrors('file')
      setValue('file', file)
   }

   const handleFileChange = (e) => {
      const file = e.target.files[0]
      validateAndSetFile(file)
   }

   const handleDragEvents = (e, highlight) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDrag(highlight)
   }

   const handleDrop = (e) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      validateAndSetFile(file)
      setIsDrag(false)
   }

   const validatePhoneNumber = (value) => {
      if (!value) {
         setError('mobile_number', {
            type: 'manual',
            message: 'Phone number is required',
         })
         return false
      }

      if (!isValidPhoneNumber(value)) {
         setError('mobile_number', {
            type: 'manual',
            message: 'Please enter a valid phone number',
         })
         return false
      }

      clearErrors('mobile_number')
      return true
   }

   return (
      <section
         className={` ${
            isContactUSPage ? 'info_tell contact_us_main' : 'comman_footer_form'
         }`}
      >
         <div className="container container-xxl">
            <div className="text-center">
               <h2 className="tech_title section_heading_title mb-2">
                  Collaborate with Benzatine Infotech
               </h2>
               <p
                  className={` mb-4 ${
                     isContactUSPage
                        ? 'info_tell_description'
                        : 'comman_footer_form_p'
                  }`}
               >
                  We’re excited to explore how we can turn your ideas into
                  impactful solutions.
               </p>
            </div>
            <div
               className={`footer_form ${isContactUSPage ? 'contact_us' : ''}`}
            >
               <form
                  id="collaborate-from"
                  onSubmit={handleSubmit(onSubmit)}
                  encType="multipart/form-data"
               >
                  <div className="form_row">
                     <div className="form_group">
                        <input
                           type="text"
                           placeholder="Name*"
                           {...register('name', {
                              required: 'Name is required',
                           })}
                        />
                        {errors.name && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.name.message}
                           </div>
                        )}
                     </div>

                     <div className="form_group">
                        <input
                           type="email"
                           placeholder="Email Address*"
                           {...register('email', {
                              required: 'Email is required',
                              pattern: {
                                 value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                 message: 'Invalid email',
                              },
                           })}
                        />
                        {errors.email && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.email.message}
                           </div>
                        )}
                     </div>
                  </div>

                  <div className="form_row">
                     <div className="form_group">
                        <PhoneInput
                           name="mobile_number"
                           international
                           countryCallingCodeEditable={true}
                           defaultCountry="IN"
                           value={phoneNumber}
                           className="mobile_number_input"
                           onChange={(value) => {
                              setPhoneNumber(value)
                              if (value) {
                                 validatePhoneNumber(value)
                                 setValue('mobile_number', value)
                              }
                           }}
                           onBlur={() => validatePhoneNumber(phoneNumber)}
                           error={errors.mobile_number?.message}
                        />
                        {errors.mobile_number && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.mobile_number.message}
                           </div>
                        )}
                     </div>
                     <div className="form_group">
                        <select
                           {...register('service', {
                              required: 'Service selection is required',
                           })}
                        >
                           <option value="">Select Services</option>
                           <option value="AI & ML Development">
                              AI & ML Development
                           </option>
                           <option value="Ecommerce Development">
                              Ecommerce Development
                           </option>
                           <option value="Web Application Development">
                              Web Application Development
                           </option>
                           <option value="Custom Software Development">
                              Custom Software Development
                           </option>
                           <option value="Progressive Web App Development">
                              Progressive Web App Development
                           </option>
                           <option value="Mobile App Development">
                              Mobile App Development
                           </option>
                           <option value="Blockchain Development">
                              Blockchain Development
                           </option>
                           <option value="Testing and QA Services">
                              Testing and QA Services
                           </option>
                           <option value="Web design Services">
                              Web design Services
                           </option>
                        </select>
                        {errors.service && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.service.message}
                           </div>
                        )}
                     </div>
                  </div>

                  <div className="form_row">
                     <div className="form_group full">
                        <textarea
                           placeholder="About Your Project*"
                           {...register('message', {
                              required: 'Message is required',
                           })}
                        ></textarea>
                        {errors.message && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.message.message}
                           </div>
                        )}
                     </div>
                  </div>

                  <div className="form_row">
                     <div className="form_group full">
                        <div
                           className={`file_upload ${
                              isDrag ? 'drag-active' : ''
                           }`}
                           onDragEnter={(e) => handleDragEvents(e, true)}
                           onDragOver={(e) => handleDragEvents(e, true)}
                           onDragLeave={(e) => handleDragEvents(e, false)}
                           onDrop={handleDrop}
                           onClick={() => fileInputRef.current.click()}
                        >
                           <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              accept=".jpg,.png,.pdf,.csv,.xls,.docx"
                              style={{ display: 'none' }}
                           />
                           <label htmlFor="file_input">
                              <DragDropIcon />
                              {file?.name ||
                                 'Drag & Drop Files Here Or Click To Upload'}
                           </label>

                           <div className="scheduled-btn-section choose_file_btn">
                              <button type="button" className="scheduled-btn">
                                 Choose File
                              </button>
                           </div>
                        </div>
                        {errors.file && (
                           <div className={`error-message ${isContactUSPage ? 'contact-us-page-error-message': ''}`}>
                              {errors.file.message}
                           </div>
                        )}
                        <p className="file_info">
                           Upload: jpg, png, pdf, csv, xls, docx file as
                           document.
                        </p>
                     </div>
                  </div>
                  <div className="d-flex justify-content-center">
                     <button
                        type="submit"
                        id="collaborate-submit"
                        className={` ${
                           isContactUSPage ? 'info_tell_btn' : 'submit_btn'
                        }`}
                        disabled={isLoading}
                     >
                        INQUIRE NOW
                        {isContactUSPage ? (
                           <svg
                              width="37"
                              height="19"
                              viewBox="0 0 37 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="arrow-icon"
                           >
                              <path
                                 d="M36.2171 9.29436L28.0355 1.11266C27.7416 0.769472 27.2251 0.729465 26.8819 1.02344C26.5387 1.31734 26.4987 1.83384 26.7927 2.17703C26.8202 2.2091 26.8499 2.23884 26.8819 2.26626L33.6645 9.05706H1.27322C0.8214 9.05706 0.455078 9.42338 0.455078 9.87527C0.455078 10.3272 0.8214 10.6934 1.27322 10.6934H33.6645L26.8819 17.476C26.5387 17.7699 26.4987 18.2864 26.7927 18.6296C27.0867 18.9728 27.6031 19.0128 27.9463 18.7188C27.9783 18.6913 28.0081 18.6616 28.0355 18.6296L36.2172 10.4479C36.5344 10.1289 36.5344 9.6135 36.2171 9.29436Z"
                                 fill="#F1ECDC"
                              />
                           </svg>
                        ) : (
                           <NavRightArrow color={'light'} />
                        )}
                     </button>
                  </div>
               </form>
            </div>
         </div>
      </section>
   )
}

export default ContactUsForm
